/**
 * Save-for-later + free-shipping progress, ported from the live theme to Horizon.
 *
 * Differences from the legacy version:
 *  - The cart drawer is `<cart-drawer-component>` (no #CartDrawer / .cart__item).
 *  - Cart row removal piggybacks on Horizon's existing remove button click —
 *    Horizon's `cart-items-component` handles the AJAX + DOM update for us, so
 *    we don't fight its render lifecycle.
 *  - Saved-grid + free-ship widgets re-render on Horizon's `cart:update` event
 *    (CartUpdateEvent dispatched from component-cart-items.js) and on drawer open.
 */

const WISHLIST_KEY = 'nani:wishlist:v1';
const WISHLIST_SYNC_ENDPOINT = '/apps/nani-app/save';
const WISHLIST_TOAST_SEEN_KEY = 'nani:wishlist-toast-seen:v1';

let wishlistToastEl = null;

// ---------- storage ----------
const getWl = () => {
  try {
    return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || { items: [] };
  } catch {
    return { items: [] };
  }
};
const setWl = (d) => localStorage.setItem(WISHLIST_KEY, JSON.stringify(d));
const hasItem = (id) => getWl().items.some((x) => String(x.id) === String(id));

const addItem = (item) => {
  const data = getWl();
  if (!data.items.some((x) => String(x.id) === String(item.id))) {
    data.items.unshift(item);
  }
  setWl(data);
  return data;
};

const removeItem = (id) => {
  const data = getWl();
  data.items = data.items.filter((x) => String(x.id) !== String(id));
  setWl(data);
  return data;
};

const hasSeenWishlistToast = () => {
  try {
    return localStorage.getItem(WISHLIST_TOAST_SEEN_KEY) === '1';
  } catch {
    return false;
  }
};
const setSeenWishlistToast = () => {
  try {
    localStorage.setItem(WISHLIST_TOAST_SEEN_KEY, '1');
  } catch {}
};

// ---------- events ----------
const dispatchWlEvent = (type, detail) => {
  window.dispatchEvent(new CustomEvent(type, { detail }));
  window.dispatchEvent(new CustomEvent('wishlist:change', { detail }));

  const items = getWl().items;
  syncWishlistToShopify(items);
  pushWishlistToCartAttribute(items).catch(() => {});
};

async function pushWishlistToCartAttribute(items) {
  await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      attributes: { _nani_wishlist_cache: JSON.stringify(items) },
    }),
  });
}

// ---------- optional cloud sync via Shopify app proxy ----------
async function syncWishlistToShopify(items) {
  const customerId =
    window.ShopifyAnalytics?.meta?.page?.customerId ||
    window.meta?.page?.customerId;
  if (!customerId) return;

  try {
    await fetch(WISHLIST_SYNC_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'wishlist', customer_id: customerId, wishlist: items }),
    });
  } catch {
    // proxy not installed or offline — localStorage is the source of truth
  }
}

async function hydrateWishlist() {
  const customerId =
    window.ShopifyAnalytics?.meta?.page?.customerId ||
    window.meta?.page?.customerId;
  if (!customerId) return;

  try {
    const response = await fetch(`${WISHLIST_SYNC_ENDPOINT}?customer_id=${customerId}`);
    if (!response.ok) return;
    const remote = await response.json().catch(() => null);
    if (!remote || !Array.isArray(remote.wishlist)) return;

    const local = getWl();
    const merged = [...remote.wishlist];
    local.items.forEach((it) => {
      if (!merged.some((r) => String(r.id) === String(it.id))) merged.push(it);
    });

    setWl({ items: merged });
    renderSavedGrid();

    if (merged.length > remote.wishlist.length) syncWishlistToShopify(merged);
  } catch {}
}

// ---------- shopify cart helpers ----------
async function shopifyFetch(url, body) {
  const res = await fetch(url, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`${url} failed: ${res.status}`);
  return res.json();
}
/**
 * Add to cart, fetch the cart drawer's section HTML in the same round-trip,
 * then dispatch a bubbling `cart:update` event so Horizon's cart-items-component
 * morphs itself with the fresh state. Without this, the drawer stays stale and
 * the newly added item appears to vanish.
 */
async function cartAddWithSections(id, quantity = 1) {
  const sectionId = getCartItemsSectionId();

  const url = sectionId
    ? `/cart/add.js?sections=${encodeURIComponent(sectionId)}`
    : '/cart/add.js';

  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ items: [{ id: Number(id), quantity }] }),
  });
  if (!res.ok) throw new Error(`/cart/add.js failed: ${res.status}`);
  const payload = await res.json();

  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: payload,
        sourceId: 'nani-save-for-later',
        data: {
          source: 'nani-save-for-later',
          itemCount: quantity,
          sections: payload.sections,
        },
      },
    })
  );
  return payload;
}

function getCartItemsSectionId() {
  const drawerCartItems = document.querySelector('cart-items-component[data-drawer]');
  return drawerCartItems?.dataset.sectionId || null;
}

// ---------- product fetch cache ----------
const ProductCache = new Map();
const ProductInflight = new Map();

async function fetchProduct(handle) {
  const key = String(handle || '');
  if (!key) throw new Error('Missing handle');
  if (ProductCache.has(key)) return ProductCache.get(key);
  if (ProductInflight.has(key)) return ProductInflight.get(key);

  const p = shopifyFetch(`/products/${encodeURIComponent(key)}.js`)
    .then((data) => {
      ProductCache.set(key, data);
      ProductInflight.delete(key);
      return data;
    })
    .catch((err) => {
      ProductInflight.delete(key);
      throw err;
    });

  ProductInflight.set(key, p);
  return p;
}

// ---------- DOM utils ----------
const q = (el, sel) => el?.querySelector(sel);
const setText = (el, v) => el && (el.textContent = v == null ? '' : String(v));
const cloneTpl = (id) => document.getElementById(id)?.content.firstElementChild.cloneNode(true);

function getItemFromButton(btn) {
  const getAttr = (name) =>
    btn.getAttribute(name) || btn.closest(`[${name}]`)?.getAttribute(name);

  const url = getAttr('data-url') || getAttr('data-product-url');
  const handleFromUrl = (u) => {
    try {
      return new URL(u, window.location.origin).pathname.match(/\/products\/([^/]+)/)?.[1] || null;
    } catch {
      return null;
    }
  };

  const primaryId = getAttr('data-variant-id') || getAttr('data-product-id');
  if (!primaryId) return null;

  const handle = getAttr('data-handle') || getAttr('data-product-handle') || handleFromUrl(url);

  return {
    id: String(primaryId),
    variant_id: getAttr('data-variant-id'),
    product_id: getAttr('data-product-id'),
    handle: handle ? decodeURIComponent(handle) : null,
    url: url || (handle ? `/products/${handle}` : null),
    title: getAttr('data-title') || getAttr('data-product-title'),
    image: getAttr('data-image') || getAttr('data-product-image'),
    // Mirrors the product's `custom.wishlist_only` metafield at save time, so the
    // saved-for-later card in the cart drawer knows not to offer Add to cart for a
    // product that isn't purchasable yet. Kept honest by refreshWishlistOnlyFlag().
    wishlist_only: getAttr('data-wishlist-only') === 'true',
    added_at: new Date().toISOString(),
  };
}

// The stored flag is a snapshot, so it goes stale the moment merch flips the
// metafield — most importantly on launch day, when a teaser becomes buyable. Any
// impression of the product (PDP or card) re-states the truth, so correct the stored
// copy whenever what we render disagrees with what we saved.
function refreshWishlistOnlyFlag(id, isWishlistOnly) {
  const data = getWl();
  const entry = data.items.find((x) => String(x.id) === String(id));
  if (!entry || entry.wishlist_only === isWishlistOnly) return;
  entry.wishlist_only = isWishlistOnly;
  setWl(data);
}

// ---------- toast ----------
function getWishlistToastEl() {
  if (wishlistToastEl && document.body?.contains(wishlistToastEl)) return wishlistToastEl;
  if (!document.body) return null;

  const toast = document.createElement('div');
  toast.className = 'WishlistCartToast';
  toast.setAttribute('aria-hidden', 'true');
  toast.innerHTML = `
    <div class="WishlistCartToast__panel" role="status" aria-label="Saved for later">
      <p class="WishlistCartToast__text">Saved for later! Find your saved items in the cart.</p>
      <button type="button" class="WishlistCartToast__link" data-wishlist-toast-cart>View cart</button>
      <button type="button" class="WishlistCartToast__dismiss" data-wishlist-toast-dismiss>Continue shopping</button>
    </div>
  `;

  toast.querySelector('[data-wishlist-toast-cart]')?.addEventListener('click', (e) => {
    e.preventDefault();
    hideWishlistToast();
    openCartDrawer();
  });
  toast.querySelector('[data-wishlist-toast-dismiss]')?.addEventListener('click', (e) => {
    e.preventDefault();
    hideWishlistToast();
  });
  toast.addEventListener('click', (e) => {
    if (e.target === toast) hideWishlistToast();
  });

  document.body.appendChild(toast);
  wishlistToastEl = toast;
  return toast;
}
function hideWishlistToast() {
  wishlistToastEl?.classList.remove('is-visible');
  wishlistToastEl?.setAttribute('aria-hidden', 'true');
}
function showWishlistToast() {
  if (hasSeenWishlistToast()) return;
  const toast = getWishlistToastEl();
  if (!toast) return;
  setSeenWishlistToast();
  toast.setAttribute('aria-hidden', 'false');
  toast.classList.add('is-visible');
}

// ---------- Horizon drawer integration ----------
function getCartDrawer() {
  return document.querySelector('cart-drawer-component');
}

function openCartDrawer() {
  const drawer = getCartDrawer();
  // Horizon exposes a public open() on the component, but firing the trigger
  // button keeps us decoupled if the API ever changes.
  const trigger = drawer?.querySelector('[data-testid="cart-drawer-trigger"]');
  if (trigger) trigger.click();
}

// ---------- save-for-later click handler ----------
function delegateSaveForLaterClicks() {
  if (document.__naniSflDelegated) return;
  document.__naniSflDelegated = true;

  document.addEventListener(
    'click',
    async (e) => {
      const btn = e.target?.closest?.('[data-save-for-later]');
      if (!btn || btn.disabled) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      const variantId = btn.getAttribute('data-variant-id');
      const row = btn.closest('[data-key]');
      btn.disabled = true;

      try {
        if (!hasItem(variantId)) {
          const item = getItemFromButton(btn) || { id: variantId, variant_id: variantId };
          addItem(item);
          dispatchWlEvent('wishlist:added', { item });
        }

        // Let Horizon do the actual cart removal — find the row's existing
        // remove button (rendered with on:click="/onLineItemRemove/<i>") and
        // click it. Its event handler triggers the cart-items-component flow
        // which updates the dialog and dispatches `cart:update`.
        const removeBtn = row?.querySelector('.cart-items__remove');
        if (removeBtn) {
          row?.classList.add('is-leaving');
          removeBtn.click();
        }

        renderSavedGrid();
      } catch (err) {
        console.error('[SaveForLater]', err);
        btn.disabled = false;
      }
    },
    true
  );
}

// ---------- wishlist toggle (PLP heart + "Add to Wishlist" CTAs) ----------
// Two markup flavours bind here:
//   [data-wishlist-toggle] - the icon-only heart on nani-product-card.
//   [data-wishlist]        - the text CTA that replaces Add to Cart on
//                            `custom.wishlist_only` products (PDP, card, and the
//                            legacy Impulse snippets ported into Horizon).
// A text CTA opts into label swapping by declaring both data-wishlist-*-text
// attributes; the heart declares neither, so its SVG is never overwritten.
const WISHLIST_TOGGLE_SEL = '[data-wishlist-toggle], [data-wishlist]';

function syncWishlistToggleButton(btn) {
  const variantId = btn.getAttribute('data-variant-id');
  const productId = btn.getAttribute('data-product-id');
  const active = hasItem(variantId) || (productId && hasItem(productId));
  btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  const title = btn.getAttribute('data-title') || 'item';
  btn.setAttribute('aria-label', `${active ? 'Remove' : 'Add'} ${title} ${active ? 'from' : 'to'} wishlist`);

  if (active) {
    const savedId = hasItem(variantId) ? variantId : productId;
    refreshWishlistOnlyFlag(savedId, btn.getAttribute('data-wishlist-only') === 'true');
  }

  const defaultText = btn.getAttribute('data-wishlist-default-text');
  const savedText = btn.getAttribute('data-wishlist-saved-text');
  if (defaultText || savedText) {
    // Write into the label span when present so sibling markup (icons, price)
    // survives; fall back to the button itself for the text-only legacy CTAs.
    setText(btn.querySelector('[data-wishlist-label]') || btn, active ? savedText || defaultText : defaultText || savedText);
    btn.classList.toggle('is-saved', active);
  }
}

function syncAllWishlistToggles() {
  document.querySelectorAll(WISHLIST_TOGGLE_SEL).forEach(syncWishlistToggleButton);
}

function delegateWishlistToggleClicks() {
  if (document.__naniWishlistToggleDelegated) return;
  document.__naniWishlistToggleDelegated = true;

  document.addEventListener('click', (e) => {
    const btn = e.target?.closest?.(WISHLIST_TOGGLE_SEL);
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const item = getItemFromButton(btn);
    if (!item) return;

    if (hasItem(item.id)) {
      removeItem(item.id);
      btn.setAttribute('aria-pressed', 'false');
      dispatchWlEvent('wishlist:removed', { item });
    } else {
      addItem(item);
      btn.setAttribute('aria-pressed', 'true');
      btn.classList.remove('is-bumping');
      // force reflow so the animation re-runs on rapid toggles
      void btn.offsetWidth;
      btn.classList.add('is-bumping');
      dispatchWlEvent('wishlist:added', { item });
    }
    syncAllWishlistToggles();
  });

  window.addEventListener('wishlist:change', syncAllWishlistToggles);

  // Re-sync after collection filters / AJAX swaps inject new cards.
  if ('MutationObserver' in window) {
    let pending = false;
    const observer = new MutationObserver((mutations) => {
      if (pending) return;
      const touched = mutations.some((m) =>
        Array.from(m.addedNodes).some(
          (n) =>
            n.nodeType === 1 &&
            (n.matches?.(WISHLIST_TOGGLE_SEL) ||
              n.querySelector?.(WISHLIST_TOGGLE_SEL))
        )
      );
      if (!touched) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        syncAllWishlistToggles();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

// ---------- saved grid rendering ----------
async function renderSavedGrid() {
  const drawer = getCartDrawer();
  if (!drawer) return;

  const containers = [
    {
      grid: drawer.querySelector('[data-cw-inline] [data-cw-saved-grid]'),
      sub: drawer.querySelector('[data-cw-inline] [data-cw-sub]'),
    },
    {
      grid: drawer.querySelector('[data-cw-inline-empty] [data-cw-saved-grid]'),
      sub: drawer.querySelector('[data-cw-inline-empty] [data-cw-sub]'),
    },
  ];

  const { items } = getWl();

  containers.forEach(({ grid, sub }) => {
    if (!grid) return;
    if (sub) sub.textContent = items.length ? `${items.length} saved` : 'No saved items yet';

    const fragment = document.createDocumentFragment();

    if (!items.length) {
      const empty = document.createElement('div');
      empty.className = 'CartWishlistEmptyState';
      empty.textContent = 'Your saved-for-later list is empty.';
      grid.replaceChildren(empty);
      return;
    }

    items.forEach((it) => {
      const card = cloneTpl('CartWishlistTplSavedItem');
      if (!card) return;

      card.dataset.id = String(it.id);
      card.classList.add('is-entering');

      const url = it.url || (it.handle ? `/products/${it.handle}` : '#');
      const titleLink = q(card, '[data-cw-title-link]');
      const img = q(card, '[data-cw-img]');
      const imgLink = q(card, '[data-cw-link]');

      if (titleLink) {
        titleLink.href = url;
        titleLink.textContent = it.title || 'Saved item';
      }
      if (imgLink) imgLink.href = url;

      if (img) {
        const incomingSrc = it.image || '';
        if (img.src !== incomingSrc) {
          img.style.opacity = '0';
          img.src = incomingSrc;
          img.onload = () => (img.style.opacity = '1');
        } else {
          img.style.opacity = '1';
        }
        img.alt = it.title || 'Saved item';
      }

      q(card, '[data-cw-remove]')?.addEventListener('click', (e) => {
        e.preventDefault();
        card.classList.add('is-leaving');
        setTimeout(() => {
          removeItem(it.id);
          dispatchWlEvent('wishlist:removed', { id: it.id });
        }, 400);
      });

      hydrateWishlistCard(card, it).catch(() => {});
      fragment.appendChild(card);
    });

    grid.replaceChildren(fragment);
  });
}

async function hydrateWishlistCard(card, it) {
  const sizeSelect = q(card, '[data-cw-size-select]');
  const priceEl = q(card, '[data-cw-price]');
  const addBtn = q(card, '[data-cw-add]');
  const statusEl = q(card, '[data-cw-status]');

  if (addBtn) addBtn.disabled = true;

  // Wishlist-only products are saveable but not buyable — drop the size picker and
  // the Add to cart button so the drawer can't route around the PDP's gate.
  if (it.wishlist_only) {
    if (sizeSelect) sizeSelect.style.display = 'none';
    if (addBtn) addBtn.style.display = 'none';
    if (priceEl) priceEl.textContent = '';
    return setText(statusEl, 'Coming soon');
  }

  const handle =
    it.handle ||
    (it.url ? new URL(it.url, window.location.origin).pathname.split('/').pop() : null);
  if (!handle) return setText(statusEl, 'Out of stock');

  try {
    const productJs = await fetchProduct(handle);
    const variants = productJs.variants || [];
    const options = productJs.options || [];

    const sizeIdx = options.findIndex((n) => {
      const label = (typeof n === 'string' ? n : n.name).toLowerCase();
      return label.includes('size') || label.includes('sizing');
    });

    const getOpt = (v, idx) => v?.[`option${idx + 1}`] || v?.options?.[idx];
    const availableVariants = variants.filter((v) => v.available);

    if (availableVariants.length === 0) {
      if (sizeSelect) sizeSelect.style.display = 'none';
      if (addBtn) addBtn.style.display = 'none';
      return setText(statusEl, 'Sold out');
    }

    if (sizeSelect) {
      sizeSelect.replaceChildren();
      sizeSelect.appendChild(new Option('Select size', '', true, true));

      if (sizeIdx === -1) {
        availableVariants.forEach((v) => sizeSelect.appendChild(new Option(v.title, v.id)));
      } else {
        const availableSizes = [...new Set(availableVariants.map((v) => getOpt(v, sizeIdx)))];
        availableSizes.forEach((sizeVal) => {
          const v = availableVariants.find((variant) => getOpt(variant, sizeIdx) === sizeVal);
          sizeSelect.appendChild(new Option(sizeVal, v.id));
        });
      }
      sizeSelect.value = '';
      setText(statusEl, 'Select a size');
    }

    const updatePrice = (vId) => {
      if (!vId) {
        if (priceEl) priceEl.textContent = '';
        if (addBtn) addBtn.disabled = true;
        setText(statusEl, 'Select a size');
        return;
      }
      const v = variants.find((x) => String(x.id) === String(vId));
      if (priceEl && v) priceEl.textContent = `$${(v.price / 100).toFixed(2)}`;
      if (addBtn) addBtn.disabled = !v?.available;
      setText(statusEl, v?.available ? '' : 'Unavailable');
    };

    updatePrice(sizeSelect ? '' : availableVariants[0].id);
    if (sizeSelect) sizeSelect.addEventListener('change', () => updatePrice(sizeSelect.value));

    if (addBtn) {
      addBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        const vId = sizeSelect ? sizeSelect.value : availableVariants[0].id;
        if (!vId) {
          setText(statusEl, 'Select a size');
          return;
        }
        addBtn.disabled = true;
        setText(statusEl, 'Adding...');
        try {
          await cartAddWithSections(vId, 1);
          card.classList.add('is-leaving');
          setTimeout(() => {
            removeItem(it.id);
            dispatchWlEvent('wishlist:removed', { id: it.id });
            refreshFreeShip();
          }, 450);
        } catch {
          addBtn.disabled = sizeSelect ? !sizeSelect.value : false;
          setText(statusEl, 'Try again');
        }
      });
    }
  } catch {
    setText(statusEl, 'Error');
  }
}

// ---------- free-shipping progress bar ----------
function moneyFormat(cents) {
  if (window.Shopify && typeof Shopify.formatMoney === 'function') {
    const fmt =
      (window.theme && window.theme.moneyFormat) || Shopify.money_format || '${{amount}}';
    return Shopify.formatMoney(cents, fmt);
  }
  return '$' + (cents / 100).toFixed(2);
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function updateFreeShipWidget(root, subtotalCents) {
  if (root.getAttribute('data-promo') === 'true') return;

  const thresholdCents = parseInt(root.getAttribute('data-threshold'), 10) || 0;
  const titleTemplate = root.getAttribute('data-title-template') || '';
  const subtitle = root.getAttribute('data-subtitle') || '';

  const fill = root.querySelector('[data-free-ship-fill]');
  const titleEl = root.querySelector('[data-free-ship-title]');
  const subtitleEl = root.querySelector('[data-free-ship-subtitle]');
  const bar = root.querySelector('.CartFreeShip__bar');

  if (!thresholdCents || thresholdCents <= 0) return;

  const remaining = Math.max(0, thresholdCents - subtotalCents);
  const progress = clamp((subtotalCents / thresholdCents) * 100, 0, 100);

  if (titleEl) {
    if (remaining > 0) {
      const amount = moneyFormat(remaining);
      titleEl.textContent = titleTemplate.includes('{{ amount }}')
        ? titleTemplate.replaceAll('{{ amount }}', amount)
        : titleTemplate || `You're ${amount} away from free shipping!`;
      if (subtitleEl) subtitleEl.textContent = subtitle;
    } else {
      titleEl.textContent = "You've unlocked FREE shipping!";
      if (subtitleEl) subtitleEl.textContent = '';
    }
  }

  if (fill) fill.style.width = progress + '%';
  if (bar) bar.setAttribute('aria-valuenow', String(Math.round(progress)));
}

async function refreshFreeShip(cartTotal) {
  const widgets = document.querySelectorAll('[data-free-ship]');
  if (!widgets.length) return;

  let subtotalCents = cartTotal;
  if (subtotalCents == null) {
    try {
      const cart = await shopifyFetch('/cart.js');
      subtotalCents = cart.total_price;
    } catch {
      return;
    }
  }
  widgets.forEach((w) => updateFreeShipWidget(w, subtotalCents));
}

// ---------- spend & save progress bar ----------
// Tiers are whole-dollar thresholds — show a clean "$75", not "$75.00".
function tierAmountLabel(cents) {
  return '$' + Math.round(cents / 100);
}

function updateSpendSave(root, subtotalCents) {
  let tiers = [];
  try {
    tiers = JSON.parse(root.getAttribute('data-tiers') || '[]');
  } catch {}
  tiers = tiers.filter((x) => x && x.t > 0).sort((a, b) => a.t - b.t);
  if (!tiers.length) return;

  const intro = root.getAttribute('data-intro') || '';
  const fill = root.querySelector('[data-ss-fill]');
  const titleEl = root.querySelector('[data-ss-title]');
  const subEl = root.querySelector('[data-ss-subtitle]');
  const ticksEl = root.querySelector('[data-ss-ticks]');
  const bar = root.querySelector('.CartSpendSave__bar');
  if (!titleEl) return;

  const maxT = tiers[tiers.length - 1].t;
  const progress = clamp((subtotalCents / maxT) * 100, 0, 100);

  // Highest unlocked tier index (-1 = none reached yet).
  let unlocked = -1;
  for (let i = 0; i < tiers.length; i++) {
    if (subtotalCents >= tiers[i].t) unlocked = i;
  }
  const next = tiers[unlocked + 1]; // undefined once all tiers unlocked

  if (unlocked === -1) {
    const amount = moneyFormat(tiers[0].t - subtotalCents);
    titleEl.textContent = 'Spend ' + amount + ' more to save ' + tiers[0].label + '!';
  } else if (next) {
    const amount = moneyFormat(next.t - subtotalCents);
    titleEl.textContent =
      '🎉 ' + tiers[unlocked].label + ' unlocked! Spend ' + amount + ' more for ' + next.label + '.';
  } else {
    titleEl.textContent = '🎉 You’ve unlocked ' + tiers[unlocked].label + ' — our biggest discount!';
  }
  if (subEl) subEl.textContent = intro;

  if (fill) fill.style.width = progress + '%';
  if (bar) bar.setAttribute('aria-valuenow', String(Math.round(progress)));

  // Build tier chips once, then just toggle unlocked state on refresh.
  if (ticksEl && !ticksEl.childElementCount) {
    tiers.forEach((tier) => {
      const item = document.createElement('div');
      item.className = 'CartSpendSave__tier';
      item.setAttribute('data-ss-tier', String(tier.t));
      item.innerHTML =
        '<span class="CartSpendSave__tierSpend">Spend ' + tierAmountLabel(tier.t) + '</span>' +
        '<span class="CartSpendSave__tierReward">' + tier.label + '</span>';
      ticksEl.appendChild(item);
    });
  }
  if (ticksEl) {
    Array.prototype.forEach.call(ticksEl.children, (item) => {
      const t = parseInt(item.getAttribute('data-ss-tier'), 10);
      item.classList.toggle('is-unlocked', subtotalCents >= t);
    });
  }
}

async function refreshSpendSave(subtotalCents) {
  const widgets = document.querySelectorAll('[data-spend-save]');
  if (!widgets.length) return;

  let cents = subtotalCents;
  if (cents == null) {
    try {
      const cart = await shopifyFetch('/cart.js');
      // items_subtotal_price is pre-cart-discount spend — use it so the bar
      // doesn't bounce back once an automatic discount applies at a tier.
      cents = cart.items_subtotal_price;
    } catch {
      return;
    }
  }
  widgets.forEach((w) => updateSpendSave(w, cents));
}

// ---------- scroll-to-saved button (in drawer header) ----------
document.addEventListener('click', (e) => {
  const btn = e.target.closest('.save-for-later-scroll');
  if (!btn) return;
  e.preventDefault();
  const drawer = getCartDrawer();
  const target = drawer?.querySelector('.CartWishlistInline');
  if (!target) return;

  const scrollContainer = (() => {
    for (
      let el = target.parentElement;
      el && drawer.contains(el);
      el = el.parentElement
    ) {
      const overflowY = getComputedStyle(el).overflowY;
      if (
        (overflowY === 'auto' || overflowY === 'scroll') &&
        el.scrollHeight > el.clientHeight
      ) {
        return el;
      }
    }
    return null;
  })();

  if (scrollContainer) {
    const top =
      target.getBoundingClientRect().top -
      scrollContainer.getBoundingClientRect().top +
      scrollContainer.scrollTop -
      12;
    scrollContainer.scrollTo({ top, behavior: 'smooth' });
  } else {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
});

// ---------- cart drawer tabs (Your Bag / Saved for Later) ----------
// Toggling + bag count were previously handled by the now-removed
// nani-free-gift.js. They live here so the tabbed drawer survives.
function setActiveTab(which) {
  document.querySelectorAll('[data-nfg-tab]').forEach((b) => {
    const on = b.getAttribute('data-nfg-tab') === which;
    b.classList.toggle('is-on', on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
  // Panels are document-scoped siblings of the tab bar, not children.
  document.querySelectorAll('[data-nfg-panel]').forEach((p) => {
    p.hidden = p.getAttribute('data-nfg-panel') !== which;
  });
}

function delegateTabClicks() {
  if (document.__naniTabsDelegated) return;
  document.__naniTabsDelegated = true;
  document.addEventListener('click', (e) => {
    const tab = e.target?.closest?.('[data-nfg-tab]');
    if (!tab) return;
    e.preventDefault();
    setActiveTab(tab.getAttribute('data-nfg-tab'));
  });
}

function updateBagCount(count) {
  if (count == null) return;
  document.querySelectorAll('[data-nfg-bag-count]').forEach((n) => {
    n.textContent = count;
  });
}

function openCartDrawerComponent() {
  const d = document.querySelector('cart-drawer-component');
  if (d && typeof d.open === 'function') d.open();
}

// Tracks cart size so we can tell an ADD (count went up) from a removal /
// quantity decrease / save-for-later. Primed on load (no open), then compared
// on every cart:update.
let lastItemCount = null;

// Re-apply the drawer bits Horizon's morph resets to server placeholders.
function applyCartUi(count, total, itemsSubtotal) {
  renderSavedGrid();
  setActiveTab('bag');
  updateBagCount(count);
  refreshFreeShip(total);
  refreshSpendSave(itemsSubtotal);
}

// ---------- init ----------
function init() {
  delegateSaveForLaterClicks();
  delegateWishlistToggleClicks();
  delegateTabClicks();
  syncAllWishlistToggles();
  renderSavedGrid();
  // Prime the cart UI + item-count baseline without opening the drawer on load.
  shopifyFetch('/cart.js')
    .then((cart) => {
      lastItemCount = cart.item_count;
      updateBagCount(cart.item_count);
      refreshFreeShip(cart.total_price);
      refreshSpendSave(cart.items_subtotal_price);
    })
    .catch(() => {});
  hydrateWishlist();

  // Horizon morphs the cart section on every cart change, replacing the tab bar,
  // bag count, saved grid — and discarding the drawer's open state. So on a cart
  // change we (1) re-apply our UI, and (2) for a genuine ADD (item count went up)
  // open the drawer AFTER the morph commits (rAF), which is why the theme's native
  // auto-open attribute alone doesn't stick here. Removals / quantity decreases /
  // save-for-later (count same or lower) never trigger the open.
  document.addEventListener('cart:update', async (e) => {
    const eventCart = e?.detail?.cart || e?.detail?.resource;
    let total = typeof eventCart?.total_price === 'number' ? eventCart.total_price : null;
    let count = typeof eventCart?.item_count === 'number' ? eventCart.item_count : null;
    let itemsSubtotal =
      typeof eventCart?.items_subtotal_price === 'number' ? eventCart.items_subtotal_price : null;
    if (total == null || count == null || itemsSubtotal == null) {
      try {
        const cart = await shopifyFetch('/cart.js');
        total = cart.total_price;
        count = cart.item_count;
        itemsSubtotal = cart.items_subtotal_price;
      } catch {}
    }

    const isAdd = lastItemCount != null && count != null && count > lastItemCount;
    if (count != null) lastItemCount = count;

    applyCartUi(count, total, itemsSubtotal);
    requestAnimationFrame(() => {
      applyCartUi(count, total, itemsSubtotal);
      if (isAdd) openCartDrawerComponent();
    });
  });
  document.addEventListener('cart:refresh', () => {
    renderSavedGrid();
    refreshFreeShip();
    refreshSpendSave();
  });
  document.addEventListener('cart:add', () => {
    refreshFreeShip();
    refreshSpendSave();
  });

  // Re-render when the drawer opens. Horizon's `dialog:open` event is dispatched
  // on the component (non-bubbling), so we hook the trigger button click instead.
  document.addEventListener('click', (e) => {
    if (e.target?.closest?.('[data-testid="cart-drawer-trigger"]')) {
      setTimeout(() => {
        renderSavedGrid();
        refreshFreeShip();
        refreshSpendSave();
      }, 50);
    }
  });

  window.addEventListener('wishlist:change', renderSavedGrid);
  window.addEventListener('wishlist:added', showWishlistToast);
}

document.readyState === 'loading'
  ? document.addEventListener('DOMContentLoaded', init)
  : init();
